import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { pricingPlansQueryOptions, productQueryOptions } from '../session/queries'
import { callPublicFunction } from '../lib/functions'
import { getProduct } from '../config'
import { EVENTS, track } from '../lib/analytics'
import { useI18n } from '../i18n/LocaleContext'
import { LanguageSwitch } from '../ui/LanguageSwitch'
import { Logo } from '../ui/Logo'
import { LoadingBlock } from '../ui/Feedback'
import { LegalNotice } from '../ui/LegalNotice'
import { PlanPicker } from '../ui/PlanPicker'

/**
 * Public pricing page. Checkout is the signup flow: there is no account until
 * Stripe reports a completed session, so this page never asks for a password
 * and collects the company name inside Stripe's own form.
 */
export function Pricing() {
  const plansQuery = useQuery(pricingPlansQueryOptions)
  const plans = plansQuery.data ?? []
  const { t, fmt, locale } = useI18n()
  // The trial Checkout will apply is products.trial_days; the configured
  // value only covers the moment before that row loads.
  const trialDays = useQuery(productQueryOptions).data?.trial_days ?? getProduct().trialDays

  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function startCheckout(planId: string) {
    if (pendingPlanId) return
    setError(null)
    setPendingPlanId(planId)

    const result = await callPublicFunction<{ checkoutUrl: string }>('stripe-checkout', {
      product: getProduct().id,
      pricingPlanId: planId,
      locale,
    })

    if (!result.ok || !result.data.checkoutUrl) {
      setError(
        !result.ok && result.code === 'plan_not_configured'
          ? t.pricing.notConfigured
          : t.pricing.checkoutError,
      )
      setPendingPlanId(null)
      return
    }
    track(EVENTS.checkoutStarted, { plan: planId })
    window.location.href = result.data.checkoutUrl
  }

  return (
    <main className="min-h-screen bg-canvas">
      <header className="flex h-16 items-center justify-between px-6">
        <Link to="/pricing">
          <Logo />
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitch />
          <Link
            to="/login"
            className="text-[13px] font-medium text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline"
          >
            {t.auth.signIn}
          </Link>
        </div>
      </header>

      <div className="mx-auto w-full max-w-4xl px-4 pb-20 pt-10">
        <div className="text-center">
          <h1 className="font-display text-[42px] font-normal tracking-tightish text-gray-900">
            {fmt(t.pricing.title, { n: trialDays })}
          </h1>
        </div>

        {plansQuery.isLoading && <LoadingBlock />}
        {plansQuery.isError && (
          <p className="mt-10 text-center text-[13px] text-red-700">{t.pricing.loadError}</p>
        )}
        {error && (
          <p className="mt-6 text-center text-[13px] text-red-700" role="alert">
            {error}
          </p>
        )}

        {plans.length > 0 && (
          <div className="mt-10">
            <PlanPicker
              plans={plans}
              trialDays={trialDays}
              pendingPlanId={pendingPlanId}
              onSelect={(planId) => void startCheckout(planId)}
            />
          </div>
        )}

        <LegalNotice className="mt-6 text-center" />

        <p className="mt-10 text-center text-[12.5px] text-gray-500">
          {t.pricing.alreadyHaveAccount}{' '}
          <Link to="/login" className="text-gray-900 underline underline-offset-2">
            {t.auth.signIn}
          </Link>
        </p>
      </div>
    </main>
  )
}

